/**
 * Evaluation Status Utilities
 * 
 * This file contains utilities for handling evaluation statuses
 * and their display in the frontend.
 */

// Evaluation status constants (align with backend enum values)
export const EVALUATION_STATUS = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  EVALUATED: 'EVALUATED'
};

// Status color mapping for UI components
export const getEvaluationStatusColor = (status) => {
  switch (status) {
    case EVALUATION_STATUS.DRAFT:
      return 'gray';
    case EVALUATION_STATUS.SUBMITTED:
      return 'green';
    case EVALUATION_STATUS.EVALUATED:
      return 'green';
    default:
      return 'gray';
  }
};

// Status label mapping (Arabic)
export const getEvaluationStatusLabel = (status) => {
  switch (status) {
    case EVALUATION_STATUS.DRAFT:
      return 'مسودة';
    case EVALUATION_STATUS.SUBMITTED:
      return 'تم التقييم';
    case EVALUATION_STATUS.EVALUATED:
      return 'تم التقييم';
    default:
      return 'مسودة';
  }
};

// Normalize any evaluation status to the supported set
export const normalizeEvaluationStatus = (status) => {
  const s = (status ?? '').toString().toUpperCase();
  if (s === EVALUATION_STATUS.EVALUATED) return EVALUATION_STATUS.EVALUATED;
  if (s === EVALUATION_STATUS.SUBMITTED) return EVALUATION_STATUS.SUBMITTED;
  return EVALUATION_STATUS.DRAFT;
};

// Role-based status display
export const getRoleBasedEvaluationStatus = (status, role) => {
  // For now, we show the same status to all roles
  // This can be expanded in the future if needed
  return getEvaluationStatusLabel(status);
};

// Nomination status constants (centralized)
export const NOMINATION_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  PENDING: 'pending',
  SHORTLISTED: 'shortlisted',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  RETURNED: 'returned'
};

// Map any obsolete/legacy statuses to the new set
export const normalizeNominationStatus = (status) => {
  if (!status) return NOMINATION_STATUS.SUBMITTED; // default
  const s = String(status).toLowerCase();
  switch (s) {
    case 'under_review':
      // Map legacy under_review to submitted (business note: control panel may show a different copy)
      return NOMINATION_STATUS.SUBMITTED;
    case 'incomplete':
      // Treat incomplete as draft
      return NOMINATION_STATUS.DRAFT;
    case 'approved':
      // Legacy synonym used in some places; map to accepted
      return NOMINATION_STATUS.ACCEPTED;
    case 'draft':
    case 'submitted':
    case 'pending':
    case 'shortlisted':
    case 'accepted':
    case 'rejected':
    case 'returned':
      return s;
    default:
      return NOMINATION_STATUS.SUBMITTED;
  }
};

// Unified color mapping for nominations (Chakra color schemes)
export const getNominationStatusColor = (status) => {
  const s = normalizeNominationStatus(status);
  switch (s) {
    case NOMINATION_STATUS.DRAFT:
      return 'gray';
    case NOMINATION_STATUS.SUBMITTED:
      return 'yellow';
    case NOMINATION_STATUS.PENDING:
      return 'purple';
    case NOMINATION_STATUS.SHORTLISTED:
      return 'blue';
    case NOMINATION_STATUS.ACCEPTED:
      return 'green';
    case NOMINATION_STATUS.REJECTED:
      return 'red';
    case NOMINATION_STATUS.RETURNED:
      return 'orange';
    default:
      return 'yellow';
  }
};

// Unified Arabic labels for nominations
export const getNominationStatusLabel = (status) => {
  const s = normalizeNominationStatus(status);
  switch (s) {
    case NOMINATION_STATUS.DRAFT:
      return 'مسودة';
    case NOMINATION_STATUS.SUBMITTED:
      return 'تحت التقييم';
    case NOMINATION_STATUS.PENDING:
      return 'في انتظار النتيجة';
    case NOMINATION_STATUS.SHORTLISTED:
      return 'قبول أولي';
    case NOMINATION_STATUS.ACCEPTED:
      return 'مقبول';
    case NOMINATION_STATUS.REJECTED:
      return 'مرفوض';
    case NOMINATION_STATUS.RETURNED:
      return 'عائد';
    default:
      return 'تحت التقييم';
  }
};

const evaluationStatusUtils = {
  EVALUATION_STATUS,
  getEvaluationStatusColor,
  getEvaluationStatusLabel,
  normalizeEvaluationStatus,
  getRoleBasedEvaluationStatus,
  // expose nomination helpers from same module for centralization
  NOMINATION_STATUS,
  normalizeNominationStatus,
  getNominationStatusColor,
  getNominationStatusLabel
};

export default evaluationStatusUtils;
